import type { ApiResponse, MonetateEvent, TrolleyActions } from '~/types/monetate/monetate.type';
import {
  extractAllRecTokens,
  extractItemsByComponent,
  getActionItems,
} from '~/utils/recommendation';
import { logDatadogAction } from '~/utils/event-logger.utils';

/**
 * Service class for interacting with the Monetate API.
 */
export default class MonetateService {
  /**
   * Calls the Monetate Engine API with the provided events.
   * @param event - Array of Monetate events to send to the API
   * @returns Promise resolving to the API response
   */
  private customClientDebugHandler: ReturnType<typeof useNuxtApp>['$customClientDebugHandler'];

  constructor() {
    const { $customClientDebugHandler } = useNuxtApp();
    this.customClientDebugHandler = $customClientDebugHandler;
  }

  async callEngineApi(
    appRegion: string = 'NL',
    event: MonetateEvent[],
    isDescisionRequest = true,
    actionTypes?: string[]
  ): Promise<ApiResponse> {
    const uuid = useRandomUUID();
    const monetateId = useCookie('mt.v').value;

    const param = {
      channel: appRegion == 'NL' ? 'a-a77f9577/p/toolstation.nl' : 'a-a77f9577/p/toolstation.be',
      monetateId: monetateId,
      events: [...event],
    };

    if (isDescisionRequest) {
      const decisionEvent: any = {
        eventType: 'monetate:decision:DecisionRequest',
        requestId: uuid,
        includeReporting: true,
      };

      if (actionTypes?.length) {
        decisionEvent.actionTypes = actionTypes;
      }

      param.events.push(decisionEvent);
    }

    try {
      const response = await useAjax<ApiResponse>(
        'https://engine.monetate.net/api/engine/v1/decide/toolstation',
        {
          method: 'post',
          params: param,
        }
      );
      const updatedMonetateId = (response as any)?.meta?.monetateId;
      if (updatedMonetateId && updatedMonetateId !== monetateId) {
        useCookie('mt.v').value = updatedMonetateId;
      }
      return response as ApiResponse;
    } catch (error: any) {
      const errorMeta = {
        param: param,
        monetateServiceError: true,
      };

      this.customClientDebugHandler(
        error,
        'Monetate Service Error - trying to call Monetate Engine API using callEngineApi()',
        errorMeta
      );

      throw error;
    }
  }

  /**
   * Tracks a page view event.
   * @param url - The URL of the page being viewed
   * @returns Promise resolving to the API response or undefined
   */
  async trackPageView(url: string): Promise<ApiResponse | undefined> {
    const eventObject = {
      eventType: 'monetate:context:PageView',
      url: url,
      pageType: url,
    };
    try {
      return this.callEngineApi('NL', [eventObject]);
    } catch (error) {
      const errorMeta = { event: eventObject, monetateServiceError: true };

      this.customClientDebugHandler(
        error,
        'Monetate Service Error - trying to track PageView using trackPageView()',
        errorMeta
      );
      return;
    }
  }

  /**
   * Handles an impression event.
   */
  async handleImpression(): Promise<void> {
    const eventObject: MonetateEvent[] = [
      // Define your impression event object structure here
    ];
    await this.callEngineApi('NL', eventObject);
  }

  private getRouteUrl(appRegion: string): string {
    const isPreview = import.meta.client ? window.location.hostname.includes('preview') : false;

    if (appRegion === 'NL') {
      return isPreview ? 'https://www.preview.toolstation.nl/' : 'https://www.toolstation.nl/';
    }
    return isPreview ? 'https://www.preview.toolstation.be/' : 'https://www.toolstation.be/';
  }

  async trackTrolleyPageView(appRegion: string) {
    const monetateStore = useMonetateStore();
    const eventObject: MonetateEvent[] = [
      {
        eventType: 'monetate:context:PageView',
        url: this.getRouteUrl(appRegion),
        pageType: 'cart',
      },
    ];

    try {
      const response = await this.callEngineApi(appRegion, eventObject, true, [
        'monetate:action:OmnichannelRecommendation',
        'monetate:action:OmnichannelJson',
      ]);

      const actions = this.filterActions(response, {
        actionType: 'monetate:action:OmnichannelRecommendation',
      });

      this.assignExperimentType(response);

      const trolleyActions: TrolleyActions = {
        recently_viewed: getActionItems('recently-viewed', actions),
        recommendations: getActionItems('recommendations', actions),
        you_may_also_like: getActionItems('you-may-also-like', actions),
        dont_miss_this: getActionItems('dont-miss-this', actions),
        often_bought_together: getActionItems('Often-bought-together', actions),
        frequently_purchased_together: getActionItems('frequently-purchased-together', actions),
        trolley_best_seller: getActionItems('trolley-best-seller', actions),
      };

      monetateStore.trolley_page_recommendations = trolleyActions;
      return true;
    } catch (e) {
      const errorMeta = { event: eventObject, monetateServiceError: true };

      this.customClientDebugHandler(
        e,
        'Monetate Service Error - trying to track trolley page using trackTrolleyPageView()',
        errorMeta
      );
      return e;
    }
  }

  async trackMyAccountView(appRegion: string) {
    const monetateStore = useMonetateStore();
    const eventObject: MonetateEvent[] = [
      {
        eventType: 'monetate:context:PageView',
        url: this.getRouteUrl(appRegion),
        pageType: 'account',
      },
    ];
    try {
      const response = await this.callEngineApi(appRegion, eventObject);

      const action = this.filterActions(response, {
        actionType: 'monetate:action:OmnichannelRecommendation',
      });

      const myAccountAction = getActionItems('recently-view', action);
      if (myAccountAction) {
        monetateStore.account_page_recommendations = myAccountAction;
      }
    } catch (e) {
      const errorMeta = { event: eventObject, monetateServiceError: true };

      this.customClientDebugHandler(
        e,
        'Monetate Service Error - trying to track my account view using trackMyAccountView()',
        errorMeta
      );
      return e;
    }
  }

  async trackProductThumbnailView(appRegion: string, recTokens: string[]) {
    const eventObject: MonetateEvent[] = [
      {
        eventType: 'monetate:record:RecImpressions',
        recImpressions: recTokens,
      },
    ];

    try {
      await this.callEngineApi(appRegion, eventObject, false);
    } catch (e) {
      const errorMeta = { event: eventObject, monetateServiceError: true };
      this.customClientDebugHandler(
        e,
        'Monetate Service Error - trying to track product thumbnail view',
        errorMeta
      );
      return e;
    }
  }

  async trackProductCardClick(appRegion: string, recToken: string) {
    const eventObject: MonetateEvent[] = [
      {
        eventType: 'monetate:record:RecClicks',
        recClicks: [recToken],
      },
    ];
    try {
      await this.callEngineApi(appRegion, eventObject, false);
    } catch (e) {
      const errorMeta = { event: eventObject, monetateServiceError: true };
      this.customClientDebugHandler(
        e,
        'Monetate Service Error - trying to track product card click using trackProductCardClick()',
        errorMeta
      );
      return e;
    }
  }

  async addToCartEvent(appRegion: string, cartLine: { [key: string]: any }) {
    const monetateStore = useMonetateStore();
    monetateStore.trolley_pop_up_recommendations = {
      ids: [],
      recTokens: [],
    };
    monetateStore.monetate_item_add_ons = [];
    let url = '';

    if (import.meta.client) {
      url = window.location.href;
    }

    const eventObject: MonetateEvent[] = [
      {
        eventType: 'monetate:context:PageView',
        url: url,
        pageType: 'product-added',
      },
      {
        eventType: 'monetate:context:Cart',
        cartLines: [cartLine],
        pageType: 'product-added',
      },
    ];

    try {
      const response = await this.callEngineApi(appRegion, eventObject);

      const actions = this.filterActions(response, {
        actionType: 'monetate:action:OmnichannelRecommendation',
      });

      const ids = extractItemsByComponent(actions, 'trolley-pop-up');
      const recTokens = extractAllRecTokens(actions);

      monetateStore.trolley_pop_up_recommendations = {
        ids,
        recTokens,
      };

      // Populate popups
      monetateStore.getproductFromId?.();

      return true;
    } catch (error) {
      const errorMeta = {
        cartLine,
        event: eventObject,
        monetateServiceError: true,
      };

      this.customClientDebugHandler(
        error,
        'Monetate Service Error - trying to track add-to-cart event using addToCartEvent()',
        errorMeta
      );

      return error;
    }
  }

  //------------ purchase event--------------------//
  async purchaseEvent(appRegion: string, purchaseLines: OrderSingleResponse[]) {
    let url = '';
    if (import.meta.client) {
      url = window.location.href;
    }
    const monetateStore = useMonetateStore();
    const eventObject: MonetateEvent[] = [
      {
        eventType: 'monetate:context:PageView',
        url: url,
        pageType: 'purchase',
      },
      {
        eventType: 'monetate:context:Purchase',
        purchaseId: new Date().getTime().toString(),
        purchaseLines: purchaseLines,
        pageType: 'purchase',
      },
    ];

    try {
      const response = await this.callEngineApi(appRegion, eventObject);

      const action = this.filterActions(response, {
        actionType: 'monetate:action:OmnichannelRecommendation',
      });

      const ids = extractItemsByComponent(action, 'Frequently-purchased-together');
      const recTokens = extractAllRecTokens(action);

      monetateStore.order_confirmation_recommendations = {
        ids,
        recTokens,
      };

      return true;
    } catch (error) {
      return error;
    }
  }

  // - getting category page actions from (trackCategoryDetailView, trackSearchPageView &  trackPLpView)
  // getting order-confirmation page actions from the purchase event response (purchaseEvent)

  // ------------- product detail view event --------------//

  async productDetailViewEvent(appRegion: string, id: string) {
    const monetateStore = useMonetateStore();
    monetateStore.social_proof_data = '';
    // Reset Monetate recommendation data
    monetateStore.pdp_similar_item = { ids: [], recTokens: [] };
    monetateStore.pdp_others_also_looked_at = { ids: [], recTokens: [] };
    monetateStore.pdp_everything_for_your_job = { ids: [], recTokens: [] };

    let url = '';
    if (import.meta.client) {
      url = window.location.href;
    }
    // Constructing the event object for the Product Detail View
    const eventObject = [
      {
        eventType: 'monetate:context:PageView',
        url: url,
        pageType: 'product',
      },
      {
        eventType: 'monetate:context:ProductDetailView',
        pageType: 'product',
        products: [
          {
            sku: id,
            productId: id,
          },
        ],
      },
    ];

    try {
      // Making the API call
      const response = await this.callEngineApi(appRegion, eventObject);

      this.assignExperimentType(response);

      // Extracting social proof data
      const socialProof: any = response?.data?.responses[0]?.actions.find(
        (action) => action.actionType === 'monetate:action:SocialProofData'
      );

      if (socialProof) {
        const count = socialProof.data?.count || 0;
        const lookbackMinutes = socialProof.data?.lookbackMinutes || 0;
        const days = Math.ceil(lookbackMinutes / (60 * 24));
        const message = `${count} ${useTranslation('PDP-boughtInLast', 'gekocht in de afgelopen')} ${days} ${days > 1 ? useTranslation('PDP-dagen', 'dagen') : useTranslation('PDP-dag', 'dag')}`;
        monetateStore.social_proof_data = message;
      }
      // Filtering actions for omnichannel recommendations
      const actions = this.filterActions(response, {
        actionType: 'monetate:action:OmnichannelRecommendation',
      });

      // Extracting recommendations for "others also looked at"
      const pdpOthersIds = extractItemsByComponent(actions, 'pdp-others');
      const pdpOthersRecTokens = extractAllRecTokens(actions);

      monetateStore.pdp_others_also_looked_at = {
        ids: pdpOthersIds,
        recTokens: pdpOthersRecTokens,
      };

      // Extracting recommendations for "everything for your job"
      const pdpEverythingIds = extractItemsByComponent(actions, 'pdp-everything');
      const pdpEverythingRecTokens = extractAllRecTokens(actions);

      monetateStore.pdp_everything_for_your_job = {
        ids: pdpEverythingIds,
        recTokens: pdpEverythingRecTokens,
      };

      // Extracting recommendations for "everything for your job"
      const pdpSimilarItemIds = extractItemsByComponent(actions, 'pdp-similar-items');
      const pdpSimilarItemRecTokens = extractAllRecTokens(actions);

      monetateStore.pdp_similar_item = {
        ids: pdpSimilarItemIds,
        recTokens: pdpSimilarItemRecTokens,
      };

      return true;
    } catch (error) {
      const errorMeta = {
        id: id,
        event: eventObject,
        monetateServiceError: true,
      };

      this.customClientDebugHandler(
        error,
        'Monetate Service Error - trying to track product detail view event using productDetailViewEvent()',
        errorMeta
      );
      return error;
    }
  }

  // ------------- search page recommendations ------------- //

  async trackSearchPageView(appRegion: string, route: ReturnType<typeof useRoute>) {
    const monetateStore = useMonetateStore();
    const url = import.meta.client ? `${window.location.origin}${route.fullPath}` : route.fullPath;
    const eventObject: MonetateEvent[] = [
      {
        eventType: 'monetate:context:PageView',
        url: url,
        pageType: 'search',
      },
    ];
    try {
      const response = await this.callEngineApi(appRegion, eventObject, true, [
        'monetate:action:OmnichannelRecommendation',
        'monetate:action:OmnichannelJson',
      ]);

      const actions = this.filterActions(response, {
        actionType: 'monetate:action:OmnichannelRecommendation',
      });

      this.assignExperimentType(response, route);

      const ids = extractItemsByComponent(actions, 'recently-viewed');
      const recTokens = extractAllRecTokens(actions);

      monetateStore.search_recommendations = {
        ids: ids,
        recTokens: recTokens,
      };
    } catch (e) {
      const errorMeta = { event: eventObject, monetateServiceError: true };

      this.customClientDebugHandler(
        e,
        'Monetate Service Error - trying to track search page view using trackSearchPageView()',
        errorMeta
      );
      return e;
    }
  }

  //------------------ Category detail view --------------- //

  async trackCategoryDetailView(appRegion: string, category: string) {
    const monetateStore = useMonetateStore();
    let url = '';
    if (import.meta.client) {
      url = window.location.href;
    }
    const monetateOptions: MonetateEvent[] = [
      {
        eventType: 'monetate:context:PageView',
        url: url,
        pageType: 'taxonomy',
      },
    ];

    try {
      const response = await this.callEngineApi(appRegion, monetateOptions);

      const actions = this.filterActions(response, {
        actionType: 'monetate:action:OmnichannelRecommendation',
      });

      this.assignExperimentType(response);

      const topSellersIds = extractItemsByComponent(actions, 'top-seller');
      const topSellerRecTokens = extractAllRecTokens(actions);

      monetateStore.category_detail_top_seller = {
        ids: topSellersIds,
        recTokens: topSellerRecTokens,
      };

      const bestSellerIds = extractItemsByComponent(actions, 'best-seller');
      const bestSellerRecTokens = extractAllRecTokens(actions);

      monetateStore.category_detail_best_seller = {
        ids: bestSellerIds,
        recTokens: bestSellerRecTokens,
      };
    } catch (e) {
      const errorMeta = { category, options: monetateOptions, monetateServiceError: true };

      this.customClientDebugHandler(
        e,
        'Monetate Service Error - trying to track category details view using trackCategoryDetailView()',
        errorMeta
      );
      return e;
    }
  }

  // -------------------- plp view----------------------------//

  async trackPLpView(appRegion: string) {
    const monetateStore = useMonetateStore();
    let url = '';
    if (import.meta.client) {
      url = window.location.href;
    }
    const eventObject: MonetateEvent[] = [
      {
        eventType: 'monetate:context:PageView',
        url: url,
        pageType: 'taxonomy',
      },
    ];

    try {
      const response = await this.callEngineApi(appRegion, eventObject);

      const actions = this.filterActions(response, {
        actionType: 'monetate:action:OmnichannelRecommendation',
      });

      this.assignExperimentType(response);

      const ids = extractItemsByComponent(actions, 'purchased-also-purchased');
      const recTokens = extractAllRecTokens(actions);

      monetateStore.plp_recommendaions = {
        ids,
        recTokens,
      };

      return true;
    } catch (error) {
      const errorMeta = {
        event: eventObject,
        monetateServiceError: true,
      };

      this.customClientDebugHandler(
        error,
        'Monetate Service Error - trying to track PLP view using trackPLpView()',
        errorMeta
      );

      return error;
    }
  }

  /**
   * Filters actions based on provided criteria.
   * @param response - The API response to filter
   * @param filterCriteria - Criteria to filter the actions
   * @returns Filtered array of actions
   */

  public filterActions(
    response: ApiResponse,
    filterCriteria: { actionType?: string; component?: string }
  ): Array<any> {
    if (!response.data || !response.data.responses || !Array.isArray(response.data.responses)) {
      return [];
    }
    return response.data.responses.flatMap((responseItem) =>
      responseItem.actions.filter((action) => {
        const hasActionType =
          typeof filterCriteria.actionType === 'string' && filterCriteria.actionType.length > 0;
        const hasComponent =
          typeof filterCriteria.component === 'string' && filterCriteria.component.length > 0;

        if (hasActionType && hasComponent) {
          return (
            action.actionType === filterCriteria.actionType ||
            action.component === filterCriteria.component
          );
        }

        if (hasActionType) {
          return action.actionType === filterCriteria.actionType;
        }

        if (hasComponent) {
          return action.component === filterCriteria.component;
        }

        return false;
      })
    );
  }

  /**
   * Assigns experiment type based on OmnichannelJson actions.
   * @param response - The API response containing OmnichannelJson actions
   * @param route - Optional route for logging
   */
  private assignExperimentType(response: ApiResponse, route?: ReturnType<typeof useRoute>) {
    const algoliaStore = useAlgoliaStore();
    const monetateJsonActions = this.filterActions(response, {
      actionType: 'monetate:action:OmnichannelJson',
    });
    const config = useRuntimeConfig();
    const featureFlags = config.public.featureFlags;

    // Disable monetate checkout flow for testing
    algoliaStore.enableNewCheckoutFlow = featureFlags.enableNewCheckoutFlow;

    // const checkoutNewFlowAction =
    //   monetateJsonActions?.find(
    //     (action: any) =>
    //       action?.json?.newCheckoutFlowJourney === 'ENABLE NEW CHECKOUT' ||
    //       action?.json?.newCheckoutFlowJourneyId === 'ENABLE NEW CHECKOUT'
    //   ) || false;

    // const checkoutOldFlowAction =
    //   monetateJsonActions?.find(
    //     (action: any) =>
    //       action?.json?.newCheckoutFlowJourney === 'ENABLE OLD CHECKOUT' ||
    //       action?.json?.newCheckoutFlowJourneyId === 'ENABLE OLD CHECKOUT'
    //   ) || false;

    // if (checkoutNewFlowAction) {
    //   algoliaStore.enableNewCheckoutFlow = true;
    // } else if (checkoutOldFlowAction) {
    //   algoliaStore.enableNewCheckoutFlow = false;
    // } else {
    //   algoliaStore.enableNewCheckoutFlow = featureFlags.enableNewCheckoutFlow || false;
    // }

    algoliaStore.enableVertex = false;
    algoliaStore.experimentType = 'default';

    if (monetateJsonActions?.length) {
      const vertexAction = monetateJsonActions.find(
        (action: any) =>
          action?.json?.searchPlatform === 'VERTEX LANE' ||
          action?.json?.searchExperimentId === 'VERTEX LANE'
      );

      if (vertexAction) {
        algoliaStore.enableVertex = true;
        algoliaStore.experimentType = 'vertex';
      } else {
        const algoliaControlAction = monetateJsonActions.find(
          (action: any) =>
            action?.json?.searchPlatform === 'ALGOLIA CONTROL' ||
            action?.json?.searchExperimentId === 'ALGOLIA CONTROL'
        );

        if (algoliaControlAction) {
          algoliaStore.experimentType = 'algolia';
        } else {
          algoliaStore.experimentType = 'default';
        }
      }
    }

    try {
      const monetateResponses = (monetateJsonActions ?? []).map((action: any) => {
        const platform = action?.json?.searchPlatform;
        const experiment = action?.json?.searchExperimentId;
        if (platform) return platform;
        if (experiment) return experiment;
        return action?.component ?? action?.actionType ?? 'unknown';
      });

      logDatadogAction('Monetate Response', {
        monetateResponses,
        monetateJsonActionsCount: monetateResponses.length,
        omnijsonFound: monetateResponses.length > 0,
        route: route?.fullPath || 'unknown',
      });
    } catch (ddErr) {
      // swallow datadog errors to avoid breaking flow
    }
  }
}

// Top selling products
// You may also like
// Others also looked at
// Similar products - for compare PDP
// Related products
// Often bought together

// New web - Add trolley - Purchased and also purchased (based on item added to trolley) - Omni

// Trolley page
// New web - Trolley - Often bought together - Omni
// New web - Trolley - Don't miss this - Omni
// New web - Trolley - Wishlist - Omni
// New web - Trolley - Purchased and also purchased (based on item added to trolley) - Omni
// New web - Trolley - Recently viewed - Omni

// HOME PAGE

// New web - Homepage - Top picks for you - Omni
// New web - Homepage - New arrivals - Omni
// New web - Homepage - Our recommendation for you - Omni
// New web - Homepage - Best deals - Omni
// New web - Homepage - Frequently bought together based on previous purchase - Omni

// FAQ PAGE
// New web - FAQ - Recently viewed - Omni

// Search Page
// New web - Search page - Recently viewed - Omni

// Thank you page
//New web - Thank you page - Recently viewed - Omni

// PDP Page
// New web - PDP - Others also looked at - Omni
// New web - PDP - Everything for your job - Omni

// PLP Page
// New web - PLP - Purchased and also purchased - Omni

// category page-plp

// New web - Category Bevestigingsmiddelen - Top selling - Omni
// New web - Category Elektrisch Gereedschap - Top selling - Omni
// New web - Category Elektra - Top selling - Omni
// New web - Category Gereedschap Accessoires - Top selling - Omni
// New web - Category Handgereedschap - Top selling - Omni
// New web - Category Hang- & sluitwerk - Top selling - Omni
// New web - Category Ijzerwaren - Top selling - Omni
// New web - Category Installatie - Top selling - Omni
// New web - Category Lijmen kitten tapes - Top selling - Omni
// New web - Category Reinigen & onderhoud - Top selling - Omni
// New web - Category Tuingereedschap - Top selling - Omni
// New web - Category Verf & accessoires - Top selling - Omni
// New web - Category Verlichting - Top selling - Omni
// New web - Category Verwarming & ventilatie - Top selling - Omni
// New web - Category Werkplaats - Top selling - Omni
// New web - Category Werkkleding & pbm - Top selling - Omni
// New web - Category Elektrisch Gereedschap - Bestsellers & deals - Omni
// New web - Category Schroeven & bevestigingsmiddelen - Bestsellers & deals - Omni

//  - Bestsellers & deals - Omni
// New web - Category Gereedschap Accessoires - Bestsellers & deals - Omni
// New web - Category Handgereedschap - Bestsellers & deals - Omni
// New web - Category Hang- & sluitwerk - Bestsellers & deals - Omni
// New web - Category Kranen & douches - Bestsellers & deals - Omni
// New web - Category Ijzerwaren - Bestsellers & deals - Omni
// New web - Category Installatie - Bestsellers & deals - Omni
// New web - Category Lijmen, kitten & tapes - Bestsellers & deals - Omni
// New web - Category Reinigen & onderhoud - Bestsellers & deals - Omni
// New web - Category Tuingereedschap - Bestsellers & deals - Omni
// New web - Category Verf & Accessoires - Bestsellers & deals - Omni
// New web - Category Verlichting - Bestsellers & deals - Omni
// New web - Category Verwarming & ventilatie - Bestsellers & deals - Omni
// New web - Category Werkplaats - Bestsellers & deals - Omni
// New web - Category Kranen & douches - Top selling - Omni
